import React, { useCallback, useMemo, useState } from 'react';
import { Button, Spin, message, Cascader, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/client';
import {
  MUTATION_UPDATE_INDIVIDUAL_PARTNER_CONTACT,
  QUERY_COUNTRIES,
  QUERY_PARTNER_CONTACT,
} from '~pages/dashboard/referral/partner/queries';

function PartnerRegisterCityEditor({ partnerId, partnerCountrySlug, currentCityId, onClose }) {
  const { t } = useTranslation();

  const [selectedCityId, setSelectedCityId] = useState(currentCityId);

  const isChina = ['cn', 'hk', 'mo', 'tw'].includes(partnerCountrySlug);

  const { loading: countriesLoading, data: countriesResponse } = useQuery(QUERY_COUNTRIES, {
    variables: {
      countrySlugs: isChina ? ['cn', 'hk', 'mo', 'tw'] : partnerCountrySlug,
      pageNumber: 1,
      pageSize: 10,
      publishedStatus: 'PUBLISHED',
    },
    onError: e => {
      message.error(e?.toString());
    },
  });

  const [updatePartnerCity, { loading: updatePartnerCityLoading }] = useMutation(
    MUTATION_UPDATE_INDIVIDUAL_PARTNER_CONTACT,
    {
      refetchQueries: [QUERY_PARTNER_CONTACT],
      onError: e => {
        message.error(e.message);
      },
    },
  );

  const handleSubmit = useCallback(async (partnerId, selectedCityId) => {
    const response = await updatePartnerCity({
      variables: { id: partnerId, cityId: selectedCityId },
    });
    if (response?.data?.updateIndividualPartnerContact?.result) {
      message.success(t('cms.referral.partner.detail.editor.action.register_city.toast.success'));
      onClose();
    }
  }, []);

  const citiesList = useMemo(() => {
    if (isChina) {
      const transferCountry = country =>
        country?.node.states.edges.map(edge => {
          return {
            value: edge.node.id,
            label: edge.node.name,
            children: edge.node.cities.edges.map(cityEdge => ({
              value: cityEdge.node.id,
              label: cityEdge.node.name,
            })),
          };
        }) || [];

      const processedCountries = countriesResponse?.countries?.edges
        .map(country => transferCountry(country))
        .reduce((prev, next) => prev.concat(next), []);
      return processedCountries || [];
    } else {
      return (
        countriesResponse?.countries?.edges[0]?.node?.cities?.edges?.map(city => ({
          label: city.node.name,
          value: city.node.id,
        })) || []
      );
    }
  }, [countriesResponse]);

  const currentSelectedCity = useMemo(() => {
    if (isChina) {
      const isSelectedCityExistInData = citiesList.findIndex(country =>
        country.children.map(city => city.value).includes(selectedCityId),
      );
      return isSelectedCityExistInData === -1
        ? []
        : [citiesList[isSelectedCityExistInData].value, selectedCityId];
    }
  }, [selectedCityId, citiesList]);

  const handleSelectCity = useCallback(selectValue => {
    if (isChina) {
      if (selectValue[1]) {
        setSelectedCityId(selectValue[1]);
      } else {
        setSelectedCityId(null);
      }
    } else {
      setSelectedCityId(selectValue);
    }
  }, []);

  const filter = useCallback((inputValue, path) => {
    return path.some(option => option.label.toLowerCase().indexOf(inputValue.toLowerCase()) > -1);
  }, []);

  return (
    <>
      <div className="partner-register-city-editor__body">
        <section className="partner-register-city-editor__section">
          {countriesLoading && !citiesList.length ? (
            <div className="partner-register-city-editor__spin">
              <Spin />
            </div>
          ) : (
            <main className="partner-register-city-editor__main">
              <div className="">
                <h3 className="partner-register-city-editor__selector-title">
                  {t('cms.referral.partner.detail.editor.register_city.selector')}
                </h3>
                <Choose>
                  <When condition={isChina}>
                    <Cascader
                      className="partner-register-city-editor__selector"
                      options={citiesList}
                      onChange={handleSelectCity}
                      value={currentSelectedCity}
                      placeholder={'Please Select City'}
                      showSearch={{ filter }}
                    />
                  </When>
                  <Otherwise>
                    <Select
                      className="partner-register-city-editor__selector"
                      onChange={handleSelectCity}
                      value={selectedCityId}
                      placeholder={'Please Select City'}
                    >
                      {citiesList.map(city => (
                        <Select.Option value={city.value}>{city.label}</Select.Option>
                      ))}
                    </Select>
                  </Otherwise>
                </Choose>
              </div>
            </main>
          )}
        </section>
      </div>
      <div className="partner-register-city-editor__footer">
        <Button
          size="large"
          className="partner-register-city-editor__cancel-button"
          onClick={onClose}
        >
          {t('cms.referral.partner.detail.editor.btn.cancel')}
        </Button>
        <Button
          type="primary"
          size="large"
          className="partner-register-city-editor__save-button"
          loading={updatePartnerCityLoading}
          onClick={() => handleSubmit(partnerId, selectedCityId)}
        >
          {t('cms.referral.partner.detail.editor.btn.save')}
        </Button>
      </div>
    </>
  );
}

export default PartnerRegisterCityEditor;
